/* eslint-disable consistent-return */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Countdown from 'react-countdown';

import { useGetContentItemQuery } from 'common/store/modules/api/content-api';
import { SvgIcon } from 'common/react/components/SvgIcon';

import { CountdownRenderer } from 'widgets/trade-radio/js/CountdownRenderer';
import { getTranslation } from 'common/react/utils/translations';
import { ConditionalWrapper } from 'common/react/components/ConditionalWrapper';
import {
    COOKIE_NAME,
    CLOSE_STREAM_END,
    SEMI_CLOSE_STREAM_END,
    FREQUENT_REFETCH_INTERVAL,
    LONG_REFETCH_INTERVAL,
    VERY_FREQUENT_REFETCH_INTERVAL
} from './constants';

const CONSTANTS = PULSE.app.common.CONSTANTS;
const TRADE_POPUP_POSITION_EVENT = 'tradePopupReposition';

export const TradeRadioMiniPopup = ({ event, config, onStreamEnded }) => {
    const [isVisible, setIsVisible] = useState(
        PULSE.core.localStorage.getStorage(COOKIE_NAME, true) !== 'true'
    );
    const [streamEnded, setStreamEnded] = useState(false);
    const [lastEventId, setLastEventId] = useState(null);
    const [hasTriggeredRefetch, setHasTriggeredRefetch] = useState(false);
    const tradeRadioPopupRef = useRef();

    let styleObj = {
        '--trade-mini-highlight': config.highlightColour,
        '--trade-mini-highlight-hover': config.highlightHoverColour,
        '--trade-mini-background-colour': config.backgroundColour,
        '--trade-mini-cta-text-colour': config.ctaTextColour
    };

    const { UPCOMING, LIVE } = PULSE.app.common.broadcast.status;

    // Get the earliest starting channel to monitor end time
    const getEarliestChannel = () => {
        if (!event.channels || event.channels.length === 0) {
            return null;
        }

        return event.channels.reduce((earliest, channel) => {
            const channelStart = new Date(channel.startDateTime).getTime();
            const earliestStart = new Date(earliest.startDateTime).getTime();
            return channelStart < earliestStart ? channel : earliest;
        });
    };

    const earliestChannel = getEarliestChannel();

    const noCTAVisible =
        event.status === LIVE &&
        !(
            event.liveChannels?.AUDIO?.length ||
            event.liveChannels?.VIDEO?.length
        );

    const { data: imageObj, isFetching: isImageFetching } =
        useGetContentItemQuery({
            type: config.image.contentType,
            id: config.image.reference
        });

    const imageURL = imageObj
        ? `${imageObj?.onDemandUrl}?width=128&height=128` // double size for retina
        : null;

    const cookieExpiry = new Date();
    cookieExpiry.setTime(cookieExpiry.getTime() + 1 * 3600 * 1000);

    // Check if we're past the earliest channel's endDateTime
    useEffect(() => {
        if (!earliestChannel?.endDateTime) {
            return;
        }

        const checkEndTime = () => {
            const currentTime = new Date().getTime();
            const endTime = new Date(earliestChannel.endDateTime).getTime();
            const timeUntilEnd = endTime - currentTime;

            if (currentTime > endTime) {
                setStreamEnded(true);
                PULSE.core.localStorage.clearStorage(
                    COOKIE_NAME,
                    true,
                    document.location.hostname
                );
                // Don't trigger refetch immediately - wait for potential next event
                return;
            }

            // Determine check interval based on time remaining
            let nextInterval;
            if (timeUntilEnd <= CLOSE_STREAM_END) {
                // Last 5 minutes: check every second
                nextInterval = VERY_FREQUENT_REFETCH_INTERVAL;
            } else if (timeUntilEnd <= SEMI_CLOSE_STREAM_END) {
                // Last 30 minutes: check every 30 seconds
                nextInterval = FREQUENT_REFETCH_INTERVAL;
            } else {
                // More than 30 minutes: check every 5 minutes
                nextInterval = LONG_REFETCH_INTERVAL;
            }

            // Set up next check
            const timeout = setTimeout(checkEndTime, nextInterval);
            return timeout;
        };

        // Start the checking process
        const initialTimeout = checkEndTime();

        return () => {
            if (initialTimeout) {
                clearTimeout(initialTimeout);
            }
        };
    }, [earliestChannel?.endDateTime, onStreamEnded]);

    // Reset streamEnded when a new event arrives
    useEffect(() => {
        if (event && earliestChannel && event.id !== lastEventId) {
            setStreamEnded(false);
            setHasTriggeredRefetch(false); // Reset refetch flag for new event
            setLastEventId(event.id);
        }
    }, [event, earliestChannel, lastEventId]);

    // Handle immediate refetch when stream ends
    useEffect(() => {
        if (streamEnded && !hasTriggeredRefetch && onStreamEnded) {
            // Trigger refetch immediately when stream ends
            setHasTriggeredRefetch(true);
            onStreamEnded();
        }
    }, [streamEnded, hasTriggeredRefetch, onStreamEnded]);

    const handleCountdownComplete = () => {
        // When countdown completes for a new event, trigger refetch for next event
        // Only if we're not in a streamEnded state (meaning this is a genuine new event countdown)
        if (!streamEnded && onStreamEnded) {
            onStreamEnded();
        }
    };

    /**
     * Handles the close action for the popup.
     */
    const handleClose = () => {
        if (tradeRadioPopupRef.current) {
            const closeEvent = new CustomEvent(
                CONSTANTS.EVENTS.TRADE_RADIO_POPUP.HIDDEN,
                {
                    bubbles: true
                }
            );
            tradeRadioPopupRef.current.dispatchEvent(closeEvent);
        }

        setIsVisible(false);

        // update cookie
        PULSE.core.localStorage.setStorage(
            COOKIE_NAME,
            'true',
            cookieExpiry,
            true,
            document.location.hostname
        );

        // tracking
        PULSE.app.tracking.recordEvent(CONSTANTS.TRACKING.EVENTS.MODAL.CLOSE, {
            /* eslint-disable camelcase */
            modal_name: 'trade radio'
            /* eslint-enable camelcase */
        });

        // emit custom event
        window.dispatchEvent(
            new CustomEvent(TRADE_POPUP_POSITION_EVENT, {
                detail: {
                    isHidden: true
                }
            })
        );
    };

    const windowRef = useRef(null);

    const openInNewWindow = (url) => {
        if (windowRef.current && !windowRef.current.closed) {
            windowRef.current.close();
        }

        windowRef.current = window.open(url, '_blank', 'width=512,height=476');
    };

    // tracking
    useEffect(() => {
        if (isVisible) {
            PULSE.app.tracking.recordEvent(
                CONSTANTS.TRACKING.EVENTS.MODAL.VIEW,
                {
                    /* eslint-disable camelcase */
                    modal_name: 'trade radio'
                    /* eslint-enable camelcase */
                }
            );
        }
    }, [isVisible]);

    useEffect(() => {
        if (tradeRadioPopupRef.current) {
            const openEvent = new CustomEvent(
                CONSTANTS.EVENTS.TRADE_RADIO_POPUP.SHOWN,
                {
                    bubbles: true
                }
            );
            tradeRadioPopupRef.current.dispatchEvent(openEvent);
        }
    }, []);

    const countDownTarget = new Date(
        Math.min(
            ...event.channels.map((channel) =>
                new Date(channel.startDateTime).getTime()
            )
        )
    ).toISOString();
    const countDownTargetTime =
        luxon.DateTime.fromISO(countDownTarget).setLocale('en-AU');

    /**
     * Handles presence of a fixed advert at the bottom of the screen by
     * setting a dynamic bottom position based on the boundingClientRect params of the advert.
     *
     * Custom events are also emitted here so that other components can position fixed elements
     * around these components.
     */
    const [bottomPos, setBottomPos] = useState(0);

    useEffect(() => {
        const observer = new MutationObserver(() => {
            const adEl = document.querySelector(
                '.advert.advert--sticky-banner'
            );

            if (adEl && window.getComputedStyle(adEl).display !== 'none') {
                const rect = adEl.getBoundingClientRect();
                const spaceFromBottom = window.innerHeight - rect.top;
                setBottomPos(spaceFromBottom);
            } else {
                setBottomPos(0);
            }

            // Emit custom event to handle repositioning of other fixed elements.
            if (tradeRadioPopupRef.current) {
                const popupRect =
                    tradeRadioPopupRef.current.getBoundingClientRect();

                window.dispatchEvent(
                    new CustomEvent(TRADE_POPUP_POSITION_EVENT, {
                        detail: {
                            position: window.innerHeight - popupRect.top
                        }
                    })
                );
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        return () => observer.disconnect();
    }, []);

    return (
        isVisible && (
            <div>
                <section
                    className="trade-radio-mini-popup"
                    style={{ ...styleObj, bottom: bottomPos }}
                    ref={tradeRadioPopupRef}
                >
                    <div className="trade-radio-mini-popup__details">
                        <div className="trade-radio-mini-popup__image placeholder placeholder--dark">
                            {!isImageFetching ? (
                                <img src={imageURL} alt={imageObj?.title} />
                            ) : null}
                        </div>
                        <div className="trade-radio-mini-popup__text">
                            <div className="trade-radio-mini-popup__header">
                                <div className="trade-radio-mini-popup__title-wrapper">
                                    {config.promo.item &&
                                    config.promo.promoItem?.onDemandUrl ? (
                                        <ConditionalWrapper
                                            condition={
                                                config.promo.links.length
                                            }
                                            wrapper={(children) => {
                                                const linkObj =
                                                    config.promo.links[0];
                                                return (
                                                    <a
                                                        href={linkObj.promoUrl}
                                                        aria-label={
                                                            linkObj.linkText
                                                        }
                                                        target={
                                                            linkObj.external
                                                                ? '_blank'
                                                                : ''
                                                        }
                                                        rel="noreferrer"
                                                        onClick={() => {
                                                            // tracking
                                                            PULSE.app.tracking.recordEvent(
                                                                PULSE.app.common
                                                                    .CONSTANTS
                                                                    .TRACKING
                                                                    .EVENTS
                                                                    .SELECT
                                                                    .SPONSOR,
                                                                {
                                                                    /* eslint-disable camelcase */
                                                                    eventData: {
                                                                        link_text:
                                                                            linkObj.linkText,
                                                                        link_url:
                                                                            linkObj.promoUrl,
                                                                        outbound:
                                                                            linkObj.external
                                                                                ? true
                                                                                : false,
                                                                        component_name:
                                                                            'trade radio popup',
                                                                        component_content_type:
                                                                            'trade',
                                                                        name: config
                                                                            .promo
                                                                            .title
                                                                    }
                                                                    /* eslint-enable camelcase */
                                                                }
                                                            );
                                                        }}
                                                    >
                                                        {children}
                                                    </a>
                                                );
                                            }}
                                        >
                                            <img
                                                className="trade-radio-mini-popup__sponsor-image"
                                                src={`${config.promo.promoItem.onDemandUrl}?height=48`}
                                                alt={config.promo.title}
                                            />
                                        </ConditionalWrapper>
                                    ) : (
                                        <h2 className="trade-radio-mini-popup__title">
                                            {config.title}
                                        </h2>
                                    )}
                                    <button
                                        onClick={() => handleClose()}
                                        className="trade-radio-mini-popup__close"
                                        aria-label="Close popup"
                                    >
                                        <SvgIcon
                                            icon="close"
                                            className="icon trade-radio-mini-popup__close-icon"
                                        />
                                    </button>
                                </div>
                                <h3 className="trade-radio-mini-popup__stream-title">
                                    {event.status === LIVE
                                        ? config.streamTitle
                                        : `${
                                              config.countdownSummaryLabel
                                          } ${`${countDownTargetTime.toRelativeCalendar()} at
                                           ${countDownTargetTime.toLocaleString(
                                               luxon.DateTime.TIME_SIMPLE
                                           )}`}`}
                                </h3>
                            </div>
                        </div>
                    </div>
                    <div className="trade-radio-mini-popup__actions">
                        {event.status === UPCOMING ? (
                            <div className="trade-radio-mini-popup__countdown">
                                {config.countdownTimerLabel}{' '}
                                <strong>
                                    <Countdown
                                        date={countDownTarget}
                                        renderer={CountdownRenderer}
                                        onComplete={handleCountdownComplete}
                                    />
                                </strong>
                            </div>
                        ) : !noCTAVisible ? (
                            <div className="trade-radio-mini-popup__cta-wrapper">
                                {event.liveChannels?.VIDEO?.length ? (
                                    <button
                                        className="trade-radio-mini-popup__cta"
                                        onClick={() => {
                                            openInNewWindow(config.videoUrl);

                                            // tracking
                                            PULSE.app.tracking.recordEvent(
                                                PULSE.app.common.CONSTANTS
                                                    .TRACKING.EVENTS.SELECT.CTA,
                                                {
                                                    /* eslint-disable camelcase */
                                                    eventData: {
                                                        link_text:
                                                            getTranslation(
                                                                'label.tradeRadio.mini.watchButton'
                                                            ),
                                                        link_url:
                                                            config.videoUrl,
                                                        outbound: false,
                                                        type: 'internal',
                                                        component_name:
                                                            'trade radio popup',
                                                        component_content_type:
                                                            'trade'
                                                    }
                                                    /* eslint-enable camelcase */
                                                }
                                            );
                                        }}
                                    >
                                        <SvgIcon
                                            className="icon"
                                            icon="video-playback-new"
                                        />{' '}
                                        {getTranslation(
                                            'label.tradeRadio.mini.watchButton'
                                        )}
                                    </button>
                                ) : (
                                    ''
                                )}
                                {event.liveChannels?.AUDIO?.length ? (
                                    <button
                                        className="trade-radio-mini-popup__cta"
                                        onClick={() => {
                                            openInNewWindow(config.audioUrl);

                                            // tracking
                                            PULSE.app.tracking.recordEvent(
                                                PULSE.app.common.CONSTANTS
                                                    .TRACKING.EVENTS.SELECT.CTA,
                                                {
                                                    /* eslint-disable camelcase */
                                                    eventData: {
                                                        link_text:
                                                            getTranslation(
                                                                'label.tradeRadio.mini.listenButton'
                                                            ),
                                                        link_url:
                                                            config.audioUrl,
                                                        outbound: false,
                                                        type: 'internal',
                                                        component_name:
                                                            'trade radio popup',
                                                        component_content_type:
                                                            'trade'
                                                    }
                                                    /* eslint-enable camelcase */
                                                }
                                            );
                                        }}
                                    >
                                        <SvgIcon
                                            className="icon"
                                            icon="listen"
                                        />{' '}
                                        {getTranslation(
                                            'label.tradeRadio.mini.listenButton'
                                        )}
                                    </button>
                                ) : (
                                    ''
                                )}
                            </div>
                        ) : null}
                        <a
                            className="trade-radio-mini-popup__cta trade-radio-mini-popup__cta--alt"
                            href={config.buttonLink}
                            onClick={() => {
                                PULSE.app.tracking.recordEvent(
                                    PULSE.app.common.CONSTANTS.TRACKING.EVENTS
                                        .SELECT.CTA,
                                    {
                                        /* eslint-disable camelcase */
                                        eventData: {
                                            link_text: config.buttonText,
                                            link_url: config.buttonLink,
                                            outbound: false,
                                            type: 'internal',
                                            component_name: 'trade radio popup',
                                            component_content_type: 'trade'
                                        }
                                        /* eslint-enable camelcase */
                                    }
                                );
                            }}
                        >
                            {config.buttonText}
                        </a>
                    </div>
                </section>
            </div>
        )
    );
};

TradeRadioMiniPopup.propTypes = {
    event: PropTypes.object.isRequired,
    config: PropTypes.object.isRequired,
    onStreamEnded: PropTypes.func
};
